import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { BORDER_RADIUS, semanticColors } from '@/theme/tokens';
import SaveStatus, { type SaveState } from './SaveStatus';
import { useSettingsControlHeight } from './SettingsSection';

interface SectionSaveRowProps {
  /** Shown only while this group differs from what's stored. */
  dirty: boolean;
  state: SaveState;
  error?: string | null;
  /** False while the edit is incomplete — e.g. a half-typed phone number. */
  canSave: boolean;
  /** Names the thing being saved, e.g. "Save name". */
  saveLabel: string;
  cancelLabel: string;
  savingLabel: string;
  savedLabel: string;
  /** Explains why Save is inert, shown in place of the status chip. */
  blockedHint?: string | null;
  onSave: () => void;
  onCancel: () => void;
  /** Keeps desktop name fields and the following section stable during edits. */
  reserveLayout?: boolean;
}

/**
 * The commit affordance for a group of text fields, rendered inside that
 * group's card. Living next to the inputs keeps it in the same scroll position
 * as the field being edited — a bar pinned to the bottom of the viewport ends
 * up behind the on-screen keyboard on mobile web, which is what this replaces.
 */
export const SectionSaveRow = ({
  dirty,
  state,
  error,
  canSave,
  saveLabel,
  cancelLabel,
  savingLabel,
  savedLabel,
  blockedHint,
  onSave,
  onCancel,
  reserveLayout = false,
}: SectionSaveRowProps) => {
  const saving = state === 'saving';
  const controlHeight = useSettingsControlHeight();

  // Once clean, the row steps aside but a just-finished "Saved" still gets its
  // moment — otherwise the confirmation unmounts the instant the save lands.
  if (!dirty && !reserveLayout) {
    if (state === 'saved' || state === 'error') {
      return (
        <Box sx={{ mt: 1.75 }}>
          <SaveStatus
            state={state}
            error={error}
            savingLabel={savingLabel}
            savedLabel={savedLabel}
          />
        </Box>
      );
    }
    return null;
  }

  const showHint = state === 'idle' && !!blockedHint && !canSave;

  return (
    <Box
      sx={
        reserveLayout
          ? {
              mt: 1,
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) auto' },
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1.5 },
              minHeight: 44,
            }
          : { mt: 2 }
      }
    >
      {(reserveLayout || showHint || state !== 'idle') && (
        <Box
          sx={
            reserveLayout
              ? { minWidth: 0, minHeight: { xs: 24, sm: 0 }, overflowWrap: 'anywhere' }
              : { mb: 1 }
          }
        >
          {showHint ? (
            <Typography sx={{ fontSize: '12px', color: semanticColors.textTertiary }}>
              {blockedHint}
            </Typography>
          ) : (
            <SaveStatus
              state={state}
              error={error}
              savingLabel={savingLabel}
              savedLabel={savedLabel}
            />
          )}
        </Box>
      )}
      <Box
        aria-hidden={reserveLayout && !dirty ? true : undefined}
        sx={{
          display: 'flex',
          gap: 0.5,
          justifyContent: 'flex-end',
          flexShrink: 0,
          visibility: reserveLayout && !dirty ? 'hidden' : 'visible',
        }}
      >
        <Button
          size="small"
          onClick={onCancel}
          disabled={saving || (reserveLayout && !dirty)}
          variant="text"
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            height: controlHeight,
            minHeight: controlHeight,
            borderRadius: BORDER_RADIUS,
            color: semanticColors.textSecondary,
            '&:hover': { background: semanticColors.overlaySubtle },
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          size="small"
          onClick={onSave}
          disabled={saving || !canSave || (reserveLayout && !dirty)}
          variant="contained"
          aria-busy={saving}
          sx={{
            position: 'relative',
            textTransform: 'none',
            fontSize: '13px',
            height: controlHeight,
            minHeight: controlHeight,
            whiteSpace: 'nowrap',
            borderRadius: BORDER_RADIUS,
            backgroundColor: semanticColors.textPrimary,
            boxShadow: 'none',
            '&:hover': { backgroundColor: semanticColors.brandAccent, boxShadow: 'none' },
            '&:active': { boxShadow: 'none' },
            '&.Mui-disabled': { backgroundColor: semanticColors.disabledBackground },
          }}
        >
          {/* Spinner overlays the hidden label, so saving keeps the width and
              no empty icon slot sits beside the text. */}
          <Box component="span" sx={{ opacity: saving ? 0 : 1 }}>
            {saveLabel}
          </Box>
          {saving && (
            <CircularProgress
              size={12}
              aria-hidden="true"
              sx={{ position: 'absolute', color: semanticColors.textOnPrimary }}
            />
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default SectionSaveRow;
